import { httpClient } from './http-client';

export interface CSVPreviewResponse {
  headers: string[];
  sample_rows: string[][];
  total_rows: number;
}

interface CSVImportRequest {
  account_id: string;
  column_map: Record<string, string>; // csv_column_index -> field_name
  rows: string[][];
  date_format: string;
  skip_duplicates: boolean;
  /**
   * The closing balance exactly as PRINTED on the statement.
   *
   * Optional, and the whole reason an imported account can hold a real
   * balance: without it the account's balance is only the net flow of
   * whatever rows happened to be in the file. Printed sign, not ledger sign
   * -- a credit-card statement shows what is owed as a positive number and
   * the server flips it.
   */
  closing_balance?: string;
}

export interface CSVImportResult {
  imported: number;
  skipped: number;
  /** Rows that reached the double-entry ledger, i.e. that moved a balance. */
  posted?: number;
  /** Whether the account was reconciled to the statement's closing balance. */
  opening_posted?: boolean;
  errors: string[] | null;
}

export class ImportExportService {
  /**
   * Upload a CSV file and get a preview of headers + sample rows
   */
  static async previewCSV(file: File): Promise<CSVPreviewResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const baseURL = import.meta.env.VITE_API_URL || '/api/v1';
    const token = (httpClient as any).defaultHeaders?.['Authorization'];

    const response = await fetch(`${baseURL}/import/preview`, {
      method: 'POST',
      headers: token ? { Authorization: token } : {},
      body: formData,
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(err.message || `Upload failed: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Import mapped CSV data as transactions
   */
  static async importCSV(req: CSVImportRequest): Promise<CSVImportResult> {
    const raw = await httpClient.post<any>('/import/csv', req);
    return (raw as any).data || raw;
  }

  /**
   * Export transactions as CSV download
   */
  static async exportCSV(params?: {
    account_id?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<void> {
    const searchParams = new URLSearchParams();
    if (params?.account_id) searchParams.set('account_id', params.account_id);
    if (params?.start_date) searchParams.set('start_date', params.start_date);
    if (params?.end_date) searchParams.set('end_date', params.end_date);

    const baseURL = import.meta.env.VITE_API_URL || '/api/v1';
    const token = (httpClient as any).defaultHeaders?.['Authorization'];
    const qs = searchParams.toString();
    const url = qs ? `${baseURL}/export/csv?${qs}` : `${baseURL}/export/csv`;

    const response = await fetch(url, {
      headers: token ? { Authorization: token } : {},
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(err.message || `Export failed: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = 'transactions.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(downloadUrl);
  }
}
